import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react'
import { logError } from '@utils/logger'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ChatBubbleLeftRightIcon, 
  XMarkIcon, 
  PaperAirplaneIcon,
  PhotoIcon,
  PlusIcon,
  DocumentTextIcon,
  PaperClipIcon,
  BookOpenIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline'
import { ChatBubbleLeftRightIcon as ChatSolidIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

import { chatAPI, ChatMessage, ChatSession } from '@/api/chat'
import { useDocumentStore } from '@/stores/documentStore'
import { citationLibraryAPI, CitationLibraryItem } from '@/api/citationLibrary'
import InputDialog from '../common/InputDialog'

/**
 * FloatingAIChat Component
 * 
 * Chat History Persistence:
 * - Document Sessions: Saved to database (chat_sessions and chat_messages tables)
 *   - Persist across browser sessions
 *   - Tied to specific documents
 *   - Can be shared/accessed from any device
 * 
 * - Explain Sessions: Stored in browser localStorage only
 *   - Temporary, tied to current browser
 *   - Not saved to database
 *   - Lost if localStorage is cleared
 *   - Can be "ported" to document chat to save permanently
 */

interface FloatingAIChatProps {
  documentId?: string
  cursorPosition?: number
  className?: string
}

export interface FloatingAIChatHandle {
  openWithContext: (context: string, initialUserMessage?: string, initialAssistantMessage?: string) => Promise<void>
  addMessage: (role: 'user' | 'assistant', content: string) => void
  showNotification: () => void
  clearNotification: () => void
}

const FloatingAIChat = forwardRef<FloatingAIChatHandle, FloatingAIChatProps>(({
  documentId,
  cursorPosition = 0,
  className
}, ref) => {
  // UI State
  const [isOpen, setIsOpen] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [message, setMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [hasNotification, setHasNotification] = useState(false)
  
  // Draggable state
  // Icon position - always starts at bottom-right, can be dragged
  const [iconPosition, setIconPosition] = useState({ x: window.innerWidth - 100, y: window.innerHeight - 100 })
  // Chat panel position - separate from icon position, adjusted when opening
  const [chatPosition, setChatPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const chatContainerRef = useRef<HTMLDivElement>(null)
  
  // Chat State
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null)
  const [sessions, setSessions] = useState<ChatSession[]>([])
  const [showSessionSelector, setShowSessionSelector] = useState(false) // Session selector dropdown
  const [selectedImages, setSelectedImages] = useState<File[]>([])
  const [selectedCitations, setSelectedCitations] = useState<CitationLibraryItem[]>([])
  const [selectedText, setSelectedText] = useState<string>('')
  const [selectedPDF, setSelectedPDF] = useState<File | null>(null)
  const [includeDocument, setIncludeDocument] = useState(true) // Toggle for including document context
  
  // Store explain sessions in localStorage (they don't have document_id)
  const EXPLAIN_SESSIONS_KEY = 'research_buddy_explain_sessions'
  
  // Context dialog state
  const [showContextDialog, setShowContextDialog] = useState(false)
  const [contextDialogType, setContextDialogType] = useState<'citation' | 'text' | 'pdf' | null>(null)
  const [availableCitations, setAvailableCitations] = useState<CitationLibraryItem[]>([])
  
  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const pdfInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  
  // Store
  const { currentDocument } = useDocumentStore()

  // Render markdown safely for chat messages
  const renderMarkdown = useCallback((content: string) => {
    try {
      const raw = marked.parse(content || '') as string
      const clean = DOMPurify.sanitize(raw)
      return { __html: clean }
    } catch (error) {
      logError('Failed to render markdown in FloatingAIChat:', error)
      // Fallback to escaped text
      const escaped = DOMPurify.sanitize(content || '')
      return { __html: escaped.replace(/\n/g, '<br />') }
    }
  }, [])

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    openWithContext: async (context: string, initialUserMessage?: string, initialAssistantMessage?: string) => {
      // Open chat if closed or minimized
      if (!isOpen) {
        setIsOpen(true)
      }
      if (isMinimized) {
        setIsMinimized(false)
      }

      // Create new session for explain task (not document-wide)
      try {
        // Create a new session without document_id (explain sessions are separate)
        const { session_id } = await chatAPI.createSession()
        const newSession: ChatSession = {
          id: session_id,
          document_id: undefined, // Explain sessions don't have document_id
          messages: [],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }

        // Add initial messages if provided
        if (initialUserMessage || initialAssistantMessage) {
          const messages: ChatMessage[] = []
          
          if (initialUserMessage) {
            messages.push({
              id: `user-${Date.now()}`,
              role: 'user',
              content: initialUserMessage,
              document_context: context,
              timestamp: new Date().toISOString()
            })
          }

          if (initialAssistantMessage) {
            messages.push({
              id: `assistant-${Date.now()}`,
              role: 'assistant',
              content: initialAssistantMessage,
              timestamp: new Date().toISOString()
            })
          }

          newSession.messages = messages
        }

        setCurrentSession(newSession)
        
        // Save explain session to localStorage
        const explainSessions = loadExplainSessions()
        explainSessions.unshift(newSession) // Add to beginning
        // Keep only last 50 explain sessions to avoid localStorage bloat
        const trimmedSessions = explainSessions.slice(0, 50)
        saveExplainSessions(trimmedSessions)
        
        // Update sessions state
        const documentSessions = sessions.filter(s => s.document_id !== undefined)
        setSessions([newSession, ...documentSessions])
        
        // Show notification
        setHasNotification(true)
        
        // Scroll to bottom
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
        }, 100)
      } catch (error) {
        logError('Failed to create explain session:', error)
      }
    },
    addMessage: (role: 'user' | 'assistant', content: string) => {
      if (!currentSession) return
      
      const newMessage: ChatMessage = {
        id: `${role}-${Date.now()}`,
        role,
        content,
        timestamp: new Date().toISOString()
      }

      const updatedSession: ChatSession = {
        ...currentSession,
        messages: [...currentSession.messages, newMessage],
        updated_at: new Date().toISOString()
      }

      setCurrentSession(updatedSession)
      
      // Update sessions state
      const updatedSessions = sessions.map(s => s.id === updatedSession.id ? updatedSession : s)
      setSessions(updatedSessions)
      
      // If it's an explain session, save to localStorage
      if (!updatedSession.document_id) {
        const explainSessions = loadExplainSessions()
        const index = explainSessions.findIndex(s => s.id === updatedSession.id)
        if (index >= 0) {
          explainSessions[index] = updatedSession
        } else {
          explainSessions.unshift(updatedSession)
        }
        saveExplainSessions(explainSessions.slice(0, 50)) // Keep only last 50
      }
      
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    },
    showNotification: () => {
      setHasNotification(true)
    },
    clearNotification: () => {
      setHasNotification(false)
    }
  }), [isOpen, isMinimized, currentSession, sessions])

  // Auto-scroll to bottom of messages
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  // Load sessions on mount or document change
  useEffect(() => {
    if (isOpen) {
      if (documentId) {
        loadDocumentSessions()
      } else {
        // Load only explain sessions if no document
        const explainSessions = loadExplainSessions()
        setSessions(explainSessions)
        if (explainSessions.length > 0) {
          setCurrentSession(explainSessions[0])
        }
      }
    }
  }, [documentId, isOpen])

  // Close session selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showSessionSelector && chatContainerRef.current) {
        const target = event.target as Node
        if (!chatContainerRef.current.contains(target)) {
          setShowSessionSelector(false)
        }
      }
    }

    if (showSessionSelector) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [showSessionSelector])

  // Scroll to bottom when messages change
  useEffect(() => {
    if (currentSession?.messages.length) {
      setTimeout(scrollToBottom, 100)
    }
  }, [currentSession?.messages, scrollToBottom])

  // Focus textarea when opened
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => textareaRef.current?.focus(), 100)
    }
  }, [isOpen, isMinimized])

  // Clear notification when chat is opened
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setHasNotification(false)
    }
  }, [isOpen, isMinimized])

  // Load citations for context dialog
  useEffect(() => {
    if (showContextDialog && contextDialogType === 'citation' && documentId) {
      loadCitations()
    }
  }, [showContextDialog, contextDialogType, documentId])

  // Load explain sessions from localStorage
  const loadExplainSessions = (): ChatSession[] => {
    try {
      const stored = localStorage.getItem(EXPLAIN_SESSIONS_KEY)
      if (stored) {
        return JSON.parse(stored) as ChatSession[]
      }
    } catch (error) {
      logError('Failed to load explain sessions from localStorage:', error)
    }
    return []
  }

  // Save explain sessions to localStorage
  const saveExplainSessions = (explainSessions: ChatSession[]) => {
    try {
      localStorage.setItem(EXPLAIN_SESSIONS_KEY, JSON.stringify(explainSessions))
    } catch (error) {
      logError('Failed to save explain sessions to localStorage:', error)
    }
  }

  const loadDocumentSessions = async () => {
    if (!documentId) return

    try {
      const documentSessions = await chatAPI.getDocumentSessions(documentId)
      const explainSessions = loadExplainSessions()
      
      // Combine document and explain sessions, sorted by updated_at (most recent first)
      const allSessions = [...documentSessions, ...explainSessions].sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at).getTime()
        const dateB = new Date(b.updated_at || b.created_at).getTime()
        return dateB - dateA
      })
      
      setSessions(allSessions)
      
      // Load the most recent session if available
      if (allSessions.length > 0) {
        setCurrentSession(allSessions[0])
      }
    } catch (error) {
      logError('Failed to load chat sessions:', error)
    }
  }

  const loadCitations = async () => {
    if (!documentId) return

    try {
      const citations = await citationLibraryAPI.getCitations(documentId)
      setAvailableCitations(citations)
    } catch (error) {
      logError('Failed to load citations:', error)
    }
  }

  const createNewSession = async () => {
    if (!documentId) return

    // Prevent creating multiple empty sessions
    // Check if current session is empty
    if (currentSession && currentSession.messages.length === 0) {
      return
    }

    // Close session selector if open
    setShowSessionSelector(false)

    try {
      const { session_id } = await chatAPI.createSession(documentId)
      const newSession: ChatSession = {
        id: session_id,
        document_id: documentId,
        messages: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
      
      setCurrentSession(newSession)
      setSessions([newSession, ...sessions])
      
      // Scroll to top (since it's a new empty session)
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    } catch (error) {
      logError('Failed to create session:', error)
    }
  }

  const deleteSession = async (sessionId: string) => {
    try {
      const sessionToDelete = sessions.find(s => s.id === sessionId)
      
      // If it's a document session, delete from backend
      if (sessionToDelete?.document_id) {
        await chatAPI.deleteSession(sessionId)
      } else {
        // If it's an explain session, remove from localStorage
        const explainSessions = loadExplainSessions()
        const filtered = explainSessions.filter(s => s.id !== sessionId)
        saveExplainSessions(filtered)
      }
      
      const remainingSessions = sessions.filter(s => s.id !== sessionId)
      setSessions(remainingSessions)
      
      if (currentSession?.id === sessionId) {
        setCurrentSession(remainingSessions.length > 0 ? remainingSessions[0] : null)
      }
    } catch (error) {
      logError('Failed to delete session:', error)
    }
  }

  const switchSession = (sessionId: string) => {
    const session = sessions.find(s => s.id === sessionId)
    if (session) {
      setCurrentSession(session)
      setShowSessionSelector(false)
      
      // Scroll to bottom
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    }
  }

  const portToDocumentChat = async () => {
    if (!documentId || !currentSession) return

    try {
      // Create new document session
      const { session_id } = await chatAPI.createSession(documentId)
      
      // Copy messages to new session
      const newSession: ChatSession = {
        id: session_id,
        document_id: documentId,
        messages: currentSession.messages,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }

      setCurrentSession(newSession)
      setSessions([newSession, ...sessions])
    } catch (error) {
      logError('Failed to port chat:', error)
    }
  }

  const sendMessage = async () => {
    if (!message.trim() && selectedImages.length === 0 && selectedCitations.length === 0 && !selectedText && !selectedPDF) return
    if (!documentId && !currentSession) {
      return
    }

    setIsLoading(true)

    try {
      // Convert images to base64
      const images: string[] = []
      for (const file of selectedImages) {
        const base64 = await chatAPI.fileToBase64(file)
        images.push(base64)
      }

      // Convert PDF to base64 if selected
      let pdfBase64: string | undefined
      if (selectedPDF) {
        pdfBase64 = await chatAPI.fileToBase64(selectedPDF)
      }

      // Build context from selected items
      let documentContext = ''
      
      if (selectedText) {
        documentContext += `Selected text: ${selectedText}\n\n`
      }

      if (selectedCitations.length > 0) {
        documentContext += `Citations:\n${selectedCitations.map(c => 
          `- ${c.title} (${c.authors.join(', ')}, ${c.year || 'n.d.'})`
        ).join('\n')}\n\n`
      }

      if (pdfBase64 && selectedPDF) {
        // Include PDF base64 data for backend extraction
        documentContext += `[PDF document attached: ${selectedPDF.name}]\n\n${pdfBase64}`
      }

      // Get document content context
      const documentContent = currentDocument?.content || ''
      const contextAtCursor = documentContent ? documentContent.slice(0, cursorPosition) : ''

      if (contextAtCursor) {
        documentContext += `Document context: ${contextAtCursor}\n\n`
      }

      const request = {
        message: message.trim() || 'Please analyze the uploaded content.',
        document_id: documentId || currentSession?.document_id,
        document_context: documentContext || contextAtCursor,
        include_full_document: includeDocument, // Use toggle state
        images: images.length > 0 ? images : undefined,
        chat_session_id: currentSession?.id
      }

      const response = await chatAPI.chat(request)

      if (response.success) {
        // Update or create session
        if (currentSession) {
          const updatedSession: ChatSession = {
            ...currentSession,
            messages: [
              ...currentSession.messages,
              {
                id: `user-${Date.now()}`,
                role: 'user',
                content: message,
                images: images.length > 0 ? images : undefined,
                document_context: documentContext || undefined,
                timestamp: new Date().toISOString()
              },
              response.message
            ],
            updated_at: new Date().toISOString()
          }
          setCurrentSession(updatedSession)
          
          // Update sessions list
          const updatedSessions = sessions.map(s => s.id === updatedSession.id ? updatedSession : s)
          setSessions(updatedSessions)
          
          // If it's an explain session, save to localStorage
          if (!updatedSession.document_id) {
            const explainSessions = loadExplainSessions()
            const index = explainSessions.findIndex(s => s.id === updatedSession.id)
            if (index >= 0) {
              explainSessions[index] = updatedSession
            } else {
              explainSessions.unshift(updatedSession)
            }
            saveExplainSessions(explainSessions.slice(0, 50)) // Keep only last 50
          }
        } else {
          // Create new session with these messages
          const newSession: ChatSession = {
            id: response.session_id,
            document_id: documentId,
            messages: [
              {
                id: `user-${Date.now()}`,
                role: 'user',
                content: message,
                images: images.length > 0 ? images : undefined,
                document_context: documentContext || undefined,
                timestamp: new Date().toISOString()
              },
              response.message
            ],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
          setCurrentSession(newSession)
          
          // Update sessions state
          const updatedSessions = [newSession, ...sessions.filter(s => s.id !== newSession.id)]
          setSessions(updatedSessions)
          
          // If it's an explain session, save to localStorage
          if (!newSession.document_id) {
            const explainSessions = loadExplainSessions()
            const index = explainSessions.findIndex(s => s.id === newSession.id)
            if (index >= 0) {
              explainSessions[index] = newSession
            } else {
              explainSessions.unshift(newSession)
            }
            saveExplainSessions(explainSessions.slice(0, 50)) // Keep only last 50
          }
        }

        // Clear input and context
        setMessage('')
        setSelectedImages([])
        setSelectedCitations([])
        setSelectedText('')
        setSelectedPDF(null)
      } else {
      }
    } catch (error) {
      logError('Failed to send message:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    const imageFiles = files.filter(file => file.type.startsWith('image/'))
    
    if (imageFiles.length !== files.length) {
    }
    
    setSelectedImages([...selectedImages, ...imageFiles])
  }

  const handlePDFUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && file.type === 'application/pdf') {
      setSelectedPDF(file)
      setShowContextDialog(false)
      setContextDialogType(null)
    } else {
    }
  }

  const removeImage = (index: number) => {
    setSelectedImages(selectedImages.filter((_, i) => i !== index))
  }

  const removeCitation = (index: number) => {
    setSelectedCitations(selectedCitations.filter((_, i) => i !== index))
  }

  const removePDF = () => {
    setSelectedPDF(null)
  }

  const handleAddCitation = (citation: CitationLibraryItem) => {
    if (!selectedCitations.find(c => c.id === citation.id)) {
      setSelectedCitations([...selectedCitations, citation])
    }
    setShowContextDialog(false)
    setContextDialogType(null)
  }

  const handleAddText = (text: string) => {
    setSelectedText(text)
    setShowContextDialog(false)
    setContextDialogType(null)
  }

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      sendMessage()
    }
  }

  // Draggable handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return // Only handle left mouse button
    if (!chatContainerRef.current) return
    
    // Don't drag if clicking on interactive elements
    const target = e.target as HTMLElement
    if (target.closest('button') || target.closest('input') || target.closest('textarea') || target.closest('a')) {
      return
    }
    
    const rect = chatContainerRef.current.getBoundingClientRect()
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    })
    setIsDragging(true)
  }, [])

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return
      
      const newX = e.clientX - dragOffset.x
      const newY = e.clientY - dragOffset.y
      
      // Constrain to viewport
      if (isOpen) {
        // Dragging chat panel
        const maxX = window.innerWidth - 384 // 384px is chat width
        const maxY = window.innerHeight - 500 // 500px is chat height
        setChatPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        })
      } else {
        // Dragging icon
        const maxX = window.innerWidth - 56 // 56px is icon size
        const maxY = window.innerHeight - 56
        setIconPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        })
      }
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.userSelect = 'none' // Prevent text selection while dragging
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.userSelect = ''
    }
  }, [isDragging, dragOffset, isOpen])

  // Reset icon position to bottom-right when chat closes
  useEffect(() => {
    if (!isOpen) {
      // Reset icon to bottom-right corner when chat closes
      setIconPosition({
        x: window.innerWidth - 100,
        y: window.innerHeight - 100
      })
    }
  }, [isOpen])

  // Reset positions on window resize
  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        if (isMinimized) {
          // Keep minimized chat at bottom-right
          setChatPosition({
            x: window.innerWidth - 384 - 20,
            y: window.innerHeight - 56 - 20
          })
        } else {
          // Adjust expanded chat position if needed
          setChatPosition(prev => ({
            x: Math.min(prev.x, window.innerWidth - 384),
            y: Math.min(prev.y, window.innerHeight - 500)
          }))
        }
      } else {
        // Keep icon at bottom-right on resize
        setIconPosition({
          x: window.innerWidth - 100,
          y: window.innerHeight - 100
        })
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isOpen, isMinimized])

  const isExplainSession = currentSession && !currentSession.document_id

  // Floating icon
  if (!isOpen) {
    return (
      <motion.div
        ref={chatContainerRef}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        style={{
          position: 'fixed',
          left: `${iconPosition.x}px`,
          top: `${iconPosition.y}px`,
          zIndex: 50,
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        className={className}
      >
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onMouseDown={handleMouseDown}
          onClick={() => {
            // Only open if not dragging
            if (!isDragging) {
              // Calculate chat position based on icon position, ensuring it fits in viewport
              const chatWidth = 384 // w-96 = 384px
              const chatHeight = 500 // h-[500px]
              const padding = 20 // Padding from edges
              
              let adjustedX = iconPosition.x
              let adjustedY = iconPosition.y
              
              // If chat would overflow right edge, align to right
              if (iconPosition.x + chatWidth > window.innerWidth - padding) {
                adjustedX = window.innerWidth - chatWidth - padding
              }
              
              // If chat would overflow bottom edge, align to bottom
              if (iconPosition.y + chatHeight > window.innerHeight - padding) {
                adjustedY = window.innerHeight - chatHeight - padding
              }
              
              // Ensure it doesn't go off the left or top edges
              adjustedX = Math.max(padding, adjustedX)
              adjustedY = Math.max(padding, adjustedY)
              
              // Set chat position (separate from icon position)
              setChatPosition({ x: adjustedX, y: adjustedY })
              setIsOpen(true)
            }
          }}
          className={clsx(
            'w-14 h-14 bg-gradient-to-r from-blue-600 to-indigo-600',
            'rounded-full shadow-lg hover:shadow-xl transition-all duration-200',
            'flex items-center justify-center text-white relative',
            isDragging && 'cursor-grabbing'
          )}
          title="Open AI Chat (Drag to move)"
        >
          <ChatSolidIcon className="w-7 h-7" />
          {hasNotification && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white"
            />
          )}
        </motion.button>
      </motion.div>
    )
  }

  return (
    <AnimatePresence>
      <motion.div
        ref={chatContainerRef}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ 
          opacity: 1, 
          scale: 1
        }}
        exit={{ opacity: 0, scale: 0.8 }}
        style={{
          position: 'fixed',
          left: isMinimized ? `${window.innerWidth - 384 - 20}px` : `${chatPosition.x}px`,
          top: isMinimized ? `${window.innerHeight - 56 - 20}px` : `${chatPosition.y}px`,
          zIndex: 50,
          cursor: isDragging ? 'grabbing' : 'default'
        }}
        className={clsx(
          'w-96 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700',
          isMinimized ? 'h-14' : 'h-[500px]',
          className
        )}
      >
        {/* Header - Draggable area */}
        <div 
          onMouseDown={handleMouseDown}
          className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-700 dark:to-gray-600 rounded-t-lg cursor-move"
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <ChatBubbleLeftRightIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
            {sessions.length > 1 ? (
              <div className="relative flex-1 min-w-0">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowSessionSelector(!showSessionSelector)
                  }}
                  className="flex items-center gap-1 text-sm font-semibold text-gray-800 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors truncate"
                  title="Switch chat session"
                >
                  <span className="truncate">
                    {isExplainSession ? 'Explain Chat' : currentDocument?.title || 'Document Chat'}
                  </span>
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* Session Selector Dropdown */}
                {showSessionSelector && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                    {sessions.length > 0 && (
                      <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                        <button
                          onClick={async () => {
                            // Delete all sessions
                            for (const session of sessions) {
                              await deleteSession(session.id)
                            }
                            setShowSessionSelector(false)
                          }}
                          className="w-full text-left text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 px-2 py-1 rounded transition-colors"
                        >
                          Clear All Chat History
                        </button>
                      </div>
                    )}
                    {sessions.map((session) => {
                      const isExplain = !session.document_id
                      const lastMessage = session.messages[session.messages.length - 1]
                      const preview = lastMessage?.content.substring(0, 50) || 'Empty chat'
                      const date = new Date(session.updated_at || session.created_at)
                      
                      return (
                        <div
                          key={session.id}
                          className={clsx(
                            'group flex items-center gap-2 px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors',
                            currentSession?.id === session.id && 'bg-blue-50 dark:bg-blue-900/20'
                          )}
                        >
                          <button
                            onClick={() => switchSession(session.id)}
                            className="flex-1 text-left min-w-0"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              {isExplain ? (
                                <span className="text-xs px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded">
                                  Explain
                                </span>
                              ) : (
                                <span className="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                                  Document
                                </span>
                              )}
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{preview}...</p>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              deleteSession(session.id)
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:text-red-700 dark:hover:text-red-400 transition-all"
                            title="Delete session"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            ) : (
              <>
                <span className="font-semibold text-gray-800 dark:text-gray-100">AI Chat</span>
                {isExplainSession && (
                  <span className="text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded">
                    Explain
                  </span>
                )}
                {currentDocument && !isExplainSession && (
                  <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-32">
                    {currentDocument.title}
                  </span>
                )}
              </>
            )}
          </div>
          
          <div className="flex items-center gap-1">
            {/* Port to document chat (for explain sessions) */}
            {isExplainSession && documentId && (
              <button
                onClick={portToDocumentChat}
                className="p-1 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                title="Port to document chat"
              >
                <DocumentIcon className="w-4 h-4" />
              </button>
            )}
            
            {/* New Session */}
            {documentId && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  createNewSession()
                }}
                className="p-1 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                title="New Chat Session"
              >
                <PlusIcon className="w-4 h-4" />
              </button>
            )}
            
            {/* Minimize */}
            <button
              onClick={() => {
                setIsMinimized(!isMinimized)
                // When minimizing, move to bottom of screen
                if (!isMinimized) {
                  setChatPosition({
                    x: window.innerWidth - 384 - 20, // 384px is chat width, 20px padding
                    y: window.innerHeight - 56 - 20  // 56px is minimized height, 20px padding
                  })
                }
              }}
              className="p-1 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title={isMinimized ? 'Expand' : 'Minimize'}
            >
              <span className="text-sm font-bold">{isMinimized ? '□' : '−'}</span>
            </button>
            
            {/* Close */}
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              title="Close Chat"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 h-64 dark:bg-gray-900">
              {currentSession?.messages.length === 0 ? (
                <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                  <ChatBubbleLeftRightIcon className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                  <p className="text-sm">Start a conversation</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Ask questions or add context</p>
                </div>
              ) : (
                currentSession?.messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={clsx(
                      'flex',
                      msg.role === 'user' ? 'justify-end' : 'justify-start'
                    )}
                  >
                    <div
                      className={clsx(
                        'max-w-[80%] p-3 rounded-lg text-sm',
                        msg.role === 'user'
                          ? 'bg-blue-600 text-white rounded-br-sm'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-bl-sm'
                      )}
                    >
                      <div
                        className={clsx(
                          'prose prose-sm max-w-none',
                          msg.role === 'user'
                            ? 'prose-invert'
                            : 'dark:prose-invert'
                        )}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={renderMarkdown(msg.content)}
                      />

                      {/* Show images for user messages */}
                      {msg.images && msg.images.length > 0 && (
                        <div className="mt-2 grid grid-cols-2 gap-1">
                          {msg.images.map((img, idx) => (
                            <img
                              key={idx}
                              src={`data:image/jpeg;base64,${img}`}
                              alt={`Uploaded ${idx + 1}`}
                              className="w-full h-16 object-cover rounded"
                            />
                          ))}
                        </div>
                      )}
                      
                      <div className={clsx(
                        'text-xs mt-1 opacity-70',
                        msg.role === 'user' ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'
                      )}>
                        {new Date(msg.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                ))
              )}
              
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-lg rounded-bl-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Context Preview */}
            {(selectedImages.length > 0 || selectedCitations.length > 0 || selectedText || selectedPDF) && (
              <div className="p-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                <div className="space-y-2">
                  {selectedText && (
                    <div className="flex items-start gap-2 text-xs">
                      <DocumentTextIcon className="w-4 h-4 text-gray-500 mt-0.5" />
                      <span className="text-gray-600 dark:text-gray-400 flex-1 truncate">{selectedText.substring(0, 50)}...</span>
                      <button onClick={() => setSelectedText('')} className="text-red-500 hover:text-red-700">×</button>
                    </div>
                  )}
                  {selectedPDF && (
                    <div className="flex items-center gap-2 text-xs">
                      <DocumentIcon className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-600 dark:text-gray-400 flex-1 truncate">{selectedPDF.name}</span>
                      <button onClick={removePDF} className="text-red-500 hover:text-red-700">×</button>
                    </div>
                  )}
                  {selectedCitations.map((citation, index) => (
                    <div key={citation.id} className="flex items-center gap-2 text-xs">
                      <BookOpenIcon className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-600 dark:text-gray-400 flex-1 truncate">{citation.title}</span>
                      <button onClick={() => removeCitation(index)} className="text-red-500 hover:text-red-700">×</button>
                    </div>
                  ))}
                  {selectedImages.map((file, index) => (
                    <div key={index} className="relative flex-shrink-0 inline-block">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        className="w-16 h-16 object-cover rounded border"
                      />
                      <button
                        onClick={() => removeImage(index)}
                        className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              {/* Include Document Toggle */}
              {documentId && (
                <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-200 dark:border-gray-700">
                  <input
                    type="checkbox"
                    id="include-document"
                    checked={includeDocument}
                    onChange={(e) => setIncludeDocument(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <label htmlFor="include-document" className="text-xs text-gray-600 dark:text-gray-400 cursor-pointer">
                    Include working document as context
                  </label>
                </div>
              )}
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <textarea
                    ref={textareaRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask about your document..."
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm dark:bg-gray-700 dark:text-gray-100"
                    rows={1}
                    disabled={isLoading}
                  />
                </div>
                
                {/* Context Menu */}
                <div className="relative">
                  <button
                    onClick={() => {
                      // Reset dialog type when opening menu
                      setContextDialogType(null)
                      setShowContextDialog(true)
                    }}
                    disabled={isLoading}
                    className="p-2 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors disabled:opacity-50"
                    title="Add Context"
                  >
                    <PaperClipIcon className="w-5 h-5" />
                  </button>
                  
                  {showContextDialog && !contextDialogType && (
                    <div className="absolute bottom-full right-0 mb-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-2 z-10">
                      <button
                        onClick={() => {
                          setContextDialogType('citation')
                          if (documentId) loadCitations()
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center gap-2"
                      >
                        <BookOpenIcon className="w-4 h-4" />
                        Add Citation
                      </button>
                      <button
                        onClick={() => setContextDialogType('text')}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center gap-2"
                      >
                        <DocumentTextIcon className="w-4 h-4" />
                        Add Text
                      </button>
                      <button
                        onClick={() => {
                          setContextDialogType('pdf')
                          pdfInputRef.current?.click()
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center gap-2"
                      >
                        <DocumentIcon className="w-4 h-4" />
                        Upload PDF
                      </button>
                      <button
                        onClick={() => {
                          fileInputRef.current?.click()
                          setShowContextDialog(false)
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center gap-2"
                      >
                        <PhotoIcon className="w-4 h-4" />
                        Upload Image
                      </button>
                    </div>
                  )}
                </div>
                
                <button
                  onClick={sendMessage}
                  disabled={isLoading || (!message.trim() && selectedImages.length === 0 && selectedCitations.length === 0 && !selectedText && !selectedPDF)}
                  className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Send Message"
                >
                  <PaperAirplaneIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Hidden file inputs */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
            <input
              ref={pdfInputRef}
              type="file"
              accept="application/pdf"
              onChange={handlePDFUpload}
              className="hidden"
            />
          </>
        )}
      </motion.div>

      {/* Context Dialogs */}
      {showContextDialog && contextDialogType === 'citation' && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={() => {
          setShowContextDialog(false)
          setContextDialogType(null)
        }}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-96 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Select Citation</h3>
            </div>
            <div className="p-4 overflow-y-auto max-h-64">
              {availableCitations.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No citations available</p>
              ) : (
                <div className="space-y-2">
                  {availableCitations.map((citation) => (
                    <button
                      key={citation.id}
                      onClick={() => handleAddCitation(citation)}
                      className="w-full text-left p-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded border border-gray-200 dark:border-gray-700"
                    >
                      <div className="font-medium text-sm text-gray-900 dark:text-gray-100">{citation.title}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {citation.authors.join(', ')} {citation.year && `(${citation.year})`}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowContextDialog(false)
                  setContextDialogType(null)
                }}
                className="w-full px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showContextDialog && contextDialogType === 'text' && (
        <InputDialog
          isOpen={true}
          onClose={() => {
            setShowContextDialog(false)
            setContextDialogType(null)
          }}
          onConfirm={handleAddText}
          title="Add Text Context"
          message="Enter text to add as context:"
          placeholder="Paste or type text here..."
          inputType="textarea"
          confirmText="Add"
          cancelText="Cancel"
        />
      )}
    </AnimatePresence>
  )
})

FloatingAIChat.displayName = 'FloatingAIChat'

export default FloatingAIChat
